// src/components/management/MemberManagement.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Search, UserPlus, Settings, MoreVertical, Crown, Users } from 'lucide-react';
import { useClassroom } from '../../contexts/ClassroomContext';

const ManagementContainer = styled.div`
  height: 100vh;
  display: flex;
  background: #f5f5f5;
`;

const Sidebar = styled.div`
  width: 300px;
  background: #2a2a2a;
  color: white;
  display: flex;
  flex-direction: column;
`;

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #444;
`;

const ClassInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
`;

const ClassAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
`;

const ClassDetails = styled.div`
  h3 {
    margin: 0 0 5px 0;
    font-size: 16px;
  }
  
  p {
    margin: 0;
    font-size: 12px;
    opacity: 0.7;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 15px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 15px 10px 40px;
  background: #333;
  border: 1px solid #444;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  
  &::placeholder {
    color: #888;
  }
  
  &:focus {
    outline: none;
    border-color: #4CAF50;
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
`;

const InviteSection = styled.div`
  padding: 15px 0;
  border-bottom: 1px solid #444;
`;

const InviteButton = styled.button`
  width: 100%;
  padding: 12px;
  background: transparent;
  border: 1px solid #4CAF50;
  border-radius: 8px;
  color: #4CAF50;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  
  &:hover {
    background: rgba(76, 175, 80, 0.1);
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ContentHeader = styled.div`
  padding: 20px;
  background: white;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: between;
  align-items: center;
`;

const QRSection = styled.div`
  margin-left: auto;
  text-align: right;
`;

const QRCode = styled.div`
  width: 80px;
  height: 80px;
  background: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 5px;
`;

const ContentBody = styled.div`
  flex: 1;
  padding: 20px;
  background: white;
`;

const MembersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const MemberSection = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 15px 0;
  color: #333;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const MemberCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px;
  background: #f9f9f9;
  border-radius: 12px;
  margin-bottom: 10px;
`;

const MemberInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const MemberAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.color || '#4CAF50'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
`;

const MemberDetails = styled.div`
  h4 {
    margin: 0 0 5px 0;
    font-size: 14px;
  }
  
  p {
    margin: 0;
    font-size: 12px;
    color: #666;
  }
`;

const MemberActions = styled.div`
  position: relative;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background: #f0f0f0;
  }
`;

const ActionMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10;
  min-width: 150px;
  
  button {
    width: 100%;
    padding: 10px 15px;
    background: none;
    border: none;
    text-align: left;
    cursor: pointer;
    
    &:hover {
      background: #f0f0f0;
    }
    
    &:first-child {
      border-radius: 8px 8px 0 0;
    }
    
    &:last-child {
      border-radius: 0 0 8px 8px;
    }
  }
`;

const MemberManagement = () => {
  const { students, addStudent, updateStudentRole, removeStudent } = useClassroom();
  const [searchTerm, setSearchTerm] = useState('');
  const [showActionMenu, setShowActionMenu] = useState(null);
  
  const teachers = students.filter(s => s.role === 'teacher');
  const studentMembers = students.filter(s => s.role === 'student');

  const handleMemberAction = async (memberId, action) => {
    switch (action) {
      case 'setTeacher':
        await updateStudentRole(memberId, 'teacher');
        break;
      case 'mute':
        // Handle mute
        break;
      case 'setAlias':
        // Handle alias
        break;
      case 'delete':
        await removeStudent(memberId);
        break;
      default:
        break;
    }
    setShowActionMenu(null);
  };

  const filteredStudents = studentMembers.filter(student =>
    student.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <ManagementContainer>
      <Sidebar>
        <SidebarHeader>
          <ClassInfo>
            <ClassAvatar>T</ClassAvatar>
            <ClassDetails>
              <h3>Tutorial</h3>
              <p>ClassIn Demo joined the class</p>
            </ClassDetails>
          </ClassInfo>
          
          <SearchContainer>
            <SearchIcon size={16} />
            <SearchInput
              placeholder="Search friends or share QR code"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchContainer>
          
          <InviteSection>
            <InviteButton>
              <UserPlus size={16} />
              Add Friends
            </InviteButton>
          </InviteSection>
        </SidebarHeader>
      </Sidebar>

      <MainContent>
        <ContentHeader>
          <div>
            <h2>Class Management</h2>
            <p>Manage class members and permissions</p>
          </div>
          
          <QRSection>
            <QRCode>QR</QRCode>
            <small>Class QR Code</small>
            <br />
            <small>Share QR code and invite friends to join the class</small>
          </QRSection>
        </ContentHeader>

        <ContentBody>
          <MemberSection>
            <SectionTitle>
              <Crown size={20} color="#FFD700" />
              Head Teacher/Teacher ({teachers.length})
            </SectionTitle>
            
            {teachers.map(teacher => (
              <MemberCard key={teacher.id}>
                <MemberInfo>
                  <MemberAvatar color="#FFD700">
                    {teacher.name.charAt(0)}
                  </MemberAvatar>
                  <MemberDetails>
                    <h4>{teacher.name}</h4>
                    <p>Head Teacher</p>
                  </MemberDetails>
                </MemberInfo>
              </MemberCard>
            ))}
          </MemberSection>

          <MemberSection>
            <SectionTitle>
              <Users size={20} />
              Students ({filteredStudents.length}/300)
            </SectionTitle>
            
            {filteredStudents.map(student => (
              <MemberCard key={student.id}>
                <MemberInfo>
                  <MemberAvatar>
                    {student.name.charAt(0)}
                  </MemberAvatar>
                  <MemberDetails>
                    <h4>{student.name}</h4>
                    <p>Student</p>
                  </MemberDetails>
                </MemberInfo>
                
                <MemberActions>
                  <ActionButton 
                    onClick={() => setShowActionMenu(
                      showActionMenu === student.id ? null : student.id
                    )}
                  >
                    <MoreVertical size={16} />
                  </ActionButton>
                  
                  {showActionMenu === student.id && (
                    <ActionMenu>
                      <button onClick={() => handleMemberAction(student.id, 'shareContact')}>
                        Share Contact Card
                      </button>
                      <button onClick={() => handleMemberAction(student.id, 'mute')}>
                        Mute
                      </button>
                      <button onClick={() => handleMemberAction(student.id, 'setAlias')}>
                        Set Class Alias
                      </button>
                      <button onClick={() => handleMemberAction(student.id, 'delete')}>
                        Delete Member
                      </button>
                      <button 
                        onClick={() => handleMemberAction(student.id, 'setTeacher')}
                        style={{ color: '#FFD700' }}
                      >
                        Set as Teacher
                      </button>
                    </ActionMenu>
                  )}
                </MemberActions>
              </MemberCard>
            ))}
          </MemberSection>
        </ContentBody>
      </MainContent>
    </ManagementContainer>
  );
};

export default MemberManagement;